import { forwardRef, ReactNode, useEffect, useMemo, useState } from 'react';

import { RiArrowDownSLine, RiErrorWarningLine } from 'react-icons/ri';

import {
  Combobox,
  ComboboxItem,
  getOptionsLockup,
  getParsedComboboxData,
  InputBase,
  SelectProps as MantineSelectProps,
  SelectFactory,
  useCombobox,
  useProps,
  useResolvedStylesApi
} from '@mantine/core';
import { useId, useUncontrolled } from '@mantine/hooks';

import { InputLoader } from '../../feedback';

import { SelectOverrideClassNames } from './Select.styles';
import { OptionsDropdown } from './optionsdropdown/OptionsDropdown';

export interface CustomComboboxItem extends ComboboxItem {
  [key: string]: any;
}

// Custom ComboboxData type that supports number values
type CustomComboboxData = Array<
  string | CustomComboboxItemWithNumber | CustomComboboxItemGroup
>;

interface CustomComboboxItemGroup {
  group: string;
  items: Array<string | CustomComboboxItemWithNumber>;
}

interface CustomComboboxItemWithNumber extends Omit<ComboboxItem, 'value'> {
  value: string | number;
  label: string;
  disabled?: boolean;
}

// Transform data with number values to string values for Mantine compatibility
const transformDataForMantine = (data: CustomComboboxData | undefined) => {
  if (!data) return data;

  return data.map((item) => {
    if (typeof item === 'string') return item;

    if ('group' in item) {
      return {
        ...item,
        items: item.items.map((subItem) => {
          if (typeof subItem === 'string') return subItem;
          return {
            ...subItem,
            value: String(subItem.value)
          };
        })
      };
    }

    return {
      ...item,
      value: String(item.value)
    };
  });
};

// Create a lookup for original values to preserve data types
const createOriginalValueLookup = (
  data: CustomComboboxData | undefined
): Record<string, string | number> => {
  const lookup: Record<string, string | number> = {};

  if (!data) return lookup;

  data.forEach((item) => {
    if (typeof item === 'string') {
      lookup[item] = item;
    } else if ('group' in item) {
      item.items.forEach((subItem) => {
        if (typeof subItem === 'string') {
          lookup[subItem] = subItem;
        } else {
          lookup[String(subItem.value)] = subItem.value;
        }
      });
    } else {
      lookup[String(item.value)] = item.value;
    }
  });

  return lookup;
};

interface SelectProps extends Omit<MantineSelectProps, 'data'> {
  loading?: boolean;
  itemComponent?: (item: CustomComboboxItem, checked?: boolean) => ReactNode;
  creatable?: boolean;
  onCreate?: (query: string) => string | number | ComboboxItem;
  getCreateLabel?: (query: string) => string;
  /**
   * Array of options for the select dropdown
   */
  data?: Array<string | CustomComboboxItemWithNumber | CustomComboboxItemGroup>;
  /**
   * Array of options for the select dropdown (alternative to data)
   */
  options?: Array<string | CustomComboboxItemWithNumber | CustomComboboxItemGroup>;
}

const defaultProps: Partial<SelectProps> = {
  searchable: false,
  creatable: false,
  withCheckIcon: false,
  allowDeselect: false,
  checkIconPosition: 'left',
  size: 'xl',
  radius: 'xs'
};

const Select = forwardRef<HTMLInputElement, SelectProps>((_props, ref) => {
  const props = useProps('Select', defaultProps, _props);
  const {
    classNames,
    styles,
    unstyled,
    loading,
    dropdownOpened,
    defaultDropdownOpened,
    onDropdownClose,
    onDropdownOpen,
    onFocus,
    onBlur,
    onClick,
    onChange,
    data,
    options,
    value,
    defaultValue,
    selectFirstOptionOnChange,
    onOptionSubmit,
    comboboxProps,
    readOnly,
    disabled,
    filter,
    limit,
    withScrollArea,
    maxDropdownHeight,
    size,
    searchable,
    rightSection,
    checkIconPosition,
    withCheckIcon,
    nothingFoundMessage,
    name,
    form,
    searchValue,
    defaultSearchValue,
    onSearchChange,
    allowDeselect,
    error,
    rightSectionPointerEvents,
    id,
    clearable,
    creatable,
    getCreateLabel,
    onCreate,
    clearButtonProps,
    hiddenInputProps,
    itemComponent,
    ...others
  } = props;

  // Use options if provided, otherwise fall back to data for backward compatibility
  const items = options || data;

  const [focussed, setFocussed] = useState<boolean>(false);

  const parsedData = useMemo(
    () => getParsedComboboxData(transformDataForMantine(items)),
    [items]
  );
  const optionsLockup = useMemo(() => getOptionsLockup(parsedData), [parsedData]);
  const originalValueLookup = useMemo(
    () => createOriginalValueLookup(items),
    [items]
  );
  const _id = useId(id);

  const [_value, setValue] = useUncontrolled({
    value,
    defaultValue,
    finalValue: null,
    onChange
  });

  const selectedOption =
    typeof _value === 'string' || typeof _value === 'number'
      ? optionsLockup[_value]
      : undefined;
  const [search, setSearch] = useUncontrolled({
    value: searchValue,
    defaultValue: defaultSearchValue,
    finalValue: selectedOption ? selectedOption.label : '',
    onChange: onSearchChange
  });

  const combobox = useCombobox({
    opened: dropdownOpened,
    defaultOpened: defaultDropdownOpened,
    onDropdownOpen,
    onDropdownClose: () => {
      onDropdownClose?.();
      combobox.resetSelectedOption();
    }
  });

  useEffect(() => {
    if (selectFirstOptionOnChange) {
      combobox.selectFirstOption();
    }
  }, [selectFirstOptionOnChange, _value]);

  useEffect(() => {
    if (defaultValue && defaultValue !== _value) {
      setValue(defaultValue);
    }
  }, [defaultValue]);

  useEffect(() => {
    if (value === null || !value) {
      setSearch('');
    }

    if (typeof value === 'string' || typeof value === 'number') {
      if (selectedOption) {
        setSearch(selectedOption.label);
      }
    }
  }, [value, selectedOption]);

  const clearButton = clearable && !!_value && !disabled && !readOnly && (
    <Combobox.ClearButton
      size={size as string}
      styles={{ root: { '--cb-icon-size': '30%' } }}
      {...clearButtonProps}
      onClear={() => {
        setValue(null);
        setSearch('');
      }}
    />
  );

  let rightSectionFinal: ReactNode | undefined = undefined;
  let enableRightSectionEvents = true;

  if (loading) {
    rightSectionFinal = <InputLoader size={size} />;
    enableRightSectionEvents = false;
  } else if (error) {
    rightSectionFinal = <RiErrorWarningLine />;
    enableRightSectionEvents = false;
  } else if (clearable) {
    if (_value) {
      rightSectionFinal = clearButton;
      enableRightSectionEvents = true;
    } else {
      rightSectionFinal = <RiArrowDownSLine />;
      enableRightSectionEvents = false;
    }
  } else if (rightSection) {
    rightSectionFinal = rightSection;
    enableRightSectionEvents = false;
  } else {
    rightSectionFinal = <RiArrowDownSLine />;
    enableRightSectionEvents = false;
  }

  const classes = useMemo(() => {
    return SelectOverrideClassNames({
      focussed,
      size,
      error,
      rightSection: rightSectionFinal
    });
  }, [focussed, size, error]);

  // keeping this instead of useMergedClassNames because of the use of combobox.
  const { resolvedClassNames, resolvedStyles } = useResolvedStylesApi<SelectFactory>(
    {
      props,
      styles,
      classNames: { ...classes, ...classNames }
    }
  );

  const handleFocus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    setFocussed(true);
    onFocus && onFocus(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
    setFocussed(false);
    onBlur && onBlur(e);
  };

  const handleSetValue = (value: string | number | null) => {
    // If value is a string, check if we have an original numeric value for it
    const originalValue =
      value && typeof value === 'string'
        ? originalValueLookup[value] ?? value
        : value;
    setValue(originalValue as any);
  };

  return (
    <>
      <Combobox
        store={combobox}
        __staticSelector="Select"
        classNames={resolvedClassNames}
        styles={resolvedStyles}
        unstyled={unstyled}
        readOnly={readOnly}
        onOptionSubmit={(val) => {
          onOptionSubmit?.(val);
          const selectedOption = optionsLockup[val];
          const selectedOriginalValue = selectedOption
            ? originalValueLookup[selectedOption.value] ?? selectedOption.value
            : null;

          const optionLockup = allowDeselect
            ? selectedOriginalValue === _value
              ? null
              : selectedOption
            : selectedOption;

          const nextValue = optionLockup
            ? originalValueLookup[optionLockup.value] ?? optionLockup.value
            : null;

          setValue(nextValue as any);
          setSearch(
            typeof nextValue === 'string' || typeof nextValue === 'number'
              ? optionLockup?.label || ''
              : ''
          );
          combobox.closeDropdown();
        }}
        size={size}
        {...comboboxProps}
      >
        <Combobox.Target targetType={searchable ? 'input' : 'button'}>
          <InputBase
            id={_id}
            ref={ref}
            rightSection={
              rightSectionFinal || (
                <Combobox.Chevron size={size} error={error} unstyled={unstyled} />
              )
            }
            rightSectionPointerEvents={
              rightSectionPointerEvents ||
              (enableRightSectionEvents ? 'all' : 'none') ||
              (clearButton ? 'all' : 'none')
            }
            {...others}
            label={others.label}
            size={size}
            __staticSelector="Select"
            disabled={disabled}
            readOnly={readOnly || !searchable}
            value={search}
            onChange={(event) => {
              setSearch(event.currentTarget.value);
              combobox.openDropdown();
              selectFirstOptionOnChange && combobox.selectFirstOption();
            }}
            onFocus={(event) => {
              searchable && combobox.openDropdown();
              handleFocus(event);
              onFocus?.(event);
            }}
            onBlur={(event) => {
              searchable && combobox.closeDropdown();
              setSearch(_value != null ? optionsLockup[_value]?.label || '' : '');
              handleBlur(event);
              onBlur?.(event);
            }}
            onClick={(event) => {
              searchable ? combobox.openDropdown() : combobox.toggleDropdown();
              onClick?.(event);
            }}
            classNames={resolvedClassNames}
            styles={resolvedStyles}
            unstyled={unstyled}
            pointer={!searchable}
            error={error}
          />
        </Combobox.Target>
        <OptionsDropdown
          data={parsedData}
          hidden={readOnly || disabled}
          filter={filter}
          search={search}
          limit={limit}
          hiddenWhenEmpty={!searchable || !nothingFoundMessage}
          withScrollArea={withScrollArea}
          maxDropdownHeight={maxDropdownHeight}
          filterOptions={searchable && selectedOption?.label !== search}
          value={_value}
          checkIconPosition={checkIconPosition}
          withCheckIcon={withCheckIcon}
          nothingFoundMessage={nothingFoundMessage}
          unstyled={unstyled}
          labelId={`${_id}-label`}
          itemComponent={itemComponent}
          creatable={
            creatable &&
            search.trim().length > 0 &&
            !items?.find((item: any) => item?.label === search)
          }
          getCreateLabel={getCreateLabel}
          onCreate={onCreate}
          setValue={handleSetValue}
        />
      </Combobox>
      <input
        type="hidden"
        name={name}
        value={_value != null ? String(_value) : ''}
        form={form}
        disabled={disabled}
        {...hiddenInputProps}
      />
    </>
  );
});

export { Select };
export type {
  SelectProps,
  CustomComboboxData,
  CustomComboboxItemWithNumber,
  CustomComboboxItemGroup
};
